/// FOURJS_START_COPYRIGHT(D,2024)
/// Property of Four Js*
/// (c) Copyright Four Js 2024, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("WindowListService", ["InitService", "MainContainerService"], function (context, cls) {
  /**
   * Main widget service
   * @namespace gbc.WindowListService
   * @gbcService
   */
  class WindowListService /** @lends gbc.WindowListService */ {
    constructor() {
      /**
       * @type {string}
       */
      this._categoryName = "window";
      /**
       * Current chromebar title item
       * @type {classes.ItemWidget}
       */
      this._currentWindowTitleItem = null;
      /**
       * Log recorder widget
       */
      this._logRecordWidget = null;
      /**
       * List of registered windows handlers
       * @type {Map<string, Function>}
       * @private
       */
      this._registeredWindowsListeners = null;
    }

    /**
     * @inheritDoc
     */
    init() {
      this._registeredWindowsListeners = new Map();
      this._floatingPanelHiddenHandler = context.MainContainerService.when(context.constants.widgetEvents.floatingPanelHidden, () => {
        this._currentWindowTitleItem?.setAriaAttribute("expanded", false);
      });
    }

    /**
     * @inheritDoc
     */
    destroy() {
      if (this._floatingPanelHiddenHandler) {
        this._floatingPanelHiddenHandler();
        this._floatingPanelHiddenHandler = null;
      }
      for (const windowListener of this._registeredWindowsListeners.values()) {
        windowListener();
      }
      this._registeredWindowsListeners.clear();
      this.disableWindowList();
      if (this._logRecordWidget && !this._logRecordWidget.isDestroyed()) {
        this._logRecordWidget.destroy();
      }
      this._logRecordWidget = null;
    }

    /**
     * Listen to window activation
     * @param {classes.WindowWidget} windowWidget
     */
    register(windowWidget) {
      // bind to window widget to update current title when window widget get displayed
      this._registeredWindowsListeners.set(
        windowWidget.getUniqueIdentifier(),
        windowWidget.when(context.constants.widgetEvents.activate, () => {
          if (!windowWidget.isModal) {
            this.setCurrentTitle(windowWidget.getText());
            this.setCurrentImage(windowWidget.getImage());
          }
          this._updateTitleButtonStatus();
        })
      );
    }

    /**
     * Stop listening to window activation
     * @param {classes.WindowWidget} windowWidget
     */
    unregister(windowWidget) {
      if (this._registeredWindowsListeners.has(windowWidget.getUniqueIdentifier())) {
        this._registeredWindowsListeners.get(windowWidget.getUniqueIdentifier())();
      }
    }

    /**
     * Get all active windows and display their title in a dropdown list
     */
    displayWindowsList() {
      const currentSession = context.SessionService.getCurrent();
      const currentApp = currentSession.getCurrentApplication();

      let windowListElements = [];
      let createdWindowItems = [];
      for (const windowNode of currentSession.getNavigationManager().getWaitingAppWindows(currentApp)) {
        // for each active window widget of the current application, create an item to add in floating panel
        // window item widget
        const windowWidget = windowNode.getWidget();
        const windowItem = cls.WidgetFactory.createWidget("Item", windowWidget.getBuildParameters());
        if (windowWidget.getImage()) {
          windowItem.setPrimaryIcon(windowWidget.getImage());
        }
        // flag the current window of the current application
        const isCurrent = windowWidget.isCurrent() && currentApp.getVMWindow()?.getWidget() === windowWidget;
        windowItem.setText(windowWidget.getText());
        windowItem.setTitle(windowWidget.getText());

        /*windowItem.onClick(() => {
          // switch of current window in main container
          const currentApp = context.SessionService.getCurrent()?.getCurrentApplication();
          currentApp?.getUI().setCurrentWindow(windowWidget.getAuiTag());
        });*/

        // prefixed window type icon (normal/modal) item
        const windowTypeItem = cls.WidgetFactory.createWidget("Item", windowWidget.getBuildParameters());
        if (windowListElements.length > 0) {
          windowTypeItem.setStyle({ "margin-left": `${(windowListElements.length - 2) * 8}px` });
        }
        if (windowListElements.length > 0) {
          // don't display arrow for first item
          windowTypeItem.setPrimaryIcon("zmdi-arrow-right-bottom");
        }
        // set window type icon (normal/modal)
        windowTypeItem.setSecondaryIcon(windowWidget.isModal ? "zmdi-card-multiple-outline" : "zmdi-card-outline");
        // set window type icon tooltip
        const windowTypeIconTooltip = windowWidget.isModal
          ? gbc.I18NService.t("gwc.main.window.dialog")
          : gbc.I18NService.t("gwc.main.window.normal");
        windowTypeItem.setTitle(windowTypeIconTooltip);

        let itemClass = `${context.constants.widgetCssClasses.windowItem}`;
        if (isCurrent) {
          itemClass += ` ${context.constants.widgetCssClasses.current}`;
        }
        const windowItems = [windowTypeItem, windowItem];
        if (isCurrent) {
          // create current window additional green label item widget
          const currentItem = cls.WidgetFactory.createWidget("Item", windowWidget.getBuildParameters());
          const currentLabel = gbc.I18NService.t("gwc.main.window.current");
          currentItem.setText(currentLabel);
          currentItem.setTitle(currentLabel);
          windowItems.push(currentItem);
        }

        // create a LI element composed of multiple items element
        const liElement = cls.DOMElementHelper.wrapElementIntoListItem(
          itemClass,
          ...windowItems.map((i) => {
            return i.getElement();
          })
        );
        // Mark the current window in the navigable window set so screen readers announce it.
        // Use "true" rather than "page" — a window is a "current item in a set", not a document page.
        if (isCurrent) {
          cls.DOMElementHelper.updateAttribute(liElement, "aria-current", "true");
        }
        // save in an array all items created to compose a window item to be able to release them on floating panel close
        createdWindowItems.push(...windowItems);
        // save in an array our LI element composed of multiple items element
        windowListElements.push(liElement);
      }
      // add all created windows items in floating panel
      const ul = document.createElement("ul");
      ul.append(...windowListElements);
      context.MainContainerService.setFloatingPanelContent(ul);
      context.MainContainerService.onFloatingPanelClose(() => {
        // on floating panel close, destroy window items
        if (createdWindowItems) {
          for (const i of createdWindowItems) {
            i.destroy();
          }
          createdWindowItems = null;
        }
      });
      // display floating panel
      let customLeftAlignmentStyle = null;
      if (cls.WindowHelper.isMobile()) {
        customLeftAlignmentStyle = { left: `${context.MainContainerService.getChromeBar().getBoundingClientRect().left}px` };
      } else {
        customLeftAlignmentStyle = { left: `${this._currentWindowTitleItem.getElement().getBoundingClientRect().left - 10}px` };
      }
      context.MainContainerService.showFloatingPanel("top", customLeftAlignmentStyle);
      this._currentWindowTitleItem?.setAriaAttribute("expanded", true);
    }

    /**
     * Set or update the current chromeBar title with the window title
     * @param {string} title
     */
    setCurrentTitle(title) {
      const currentParentApp = context.SessionService.getCurrent().getCurrentApplication();
      context.HostService.setDocumentTitle(title, currentParentApp);

      if (!title || context.ApplicationListService.isApplicationListInChromeBar()) {
        return;
      }
      if (!this._currentWindowTitleItem) {
        this._createCurrentWindowTitleItem();
      }
      this._currentWindowTitleItem.setText(title);
      this._updateTitleButtonStatus();
    }

    /**
     * Set or update the current chromeBar title icon
     * @param {string} icon
     */
    setCurrentImage(icon) {
      if (!icon || context.ApplicationListService.isApplicationListInChromeBar()) {
        return;
      }
      if (!this._currentWindowTitleItem) {
        this._createCurrentWindowTitleItem();
      }
      this._currentWindowTitleItem.setPrimaryIcon(icon);
      this._updateTitleButtonStatus();
    }

    /**
     * Enable button on current window title if current app has multiple windows, disable if single window
     * @private
     */
    _updateTitleButtonStatus() {
      if (this._currentWindowTitleItem) {
        const currentSession = context.SessionService.getCurrent();
        // get windowListVisible 4ST definition of current app
        const currentApp = currentSession.getCurrentApplication();
        const refApp = currentSession.getNavigationManager().getWaitingApplicationsHierarchy(currentApp)[0] || currentApp;
        const fullWindowsList = currentSession.getNavigationManager().getWaitingAppWindows(currentApp);
        const hasMultipleWindows = refApp.isWindowListVisible() && fullWindowsList?.length > 1;
        if (hasMultipleWindows) {
          this._currentWindowTitleItem.addClasses("multiple-window");
        } else {
          this._currentWindowTitleItem.removeClass("multiple-window");
        }
        this._setTitleButtonInteractive(hasMultipleWindows);
      }
    }

    /**
     * Toggle the chromebar title between an interactive window switcher and a plain label.
     * When not interactive, disable the button (out of Tab order, no activation) and drop the
     * popup ARIA so it isn't announced as a dialog trigger.
     * @param {boolean} interactive true when the title acts as a window switcher
     * @private
     */
    _setTitleButtonInteractive(interactive) {
      this._currentWindowTitleItem.getElement().toggleAttribute("disabled", !interactive);
      if (interactive) {
        // FloatingPanel is a <div role="dialog"> — the popup type announced via aria-haspopup
        // must match the role of the element referenced by aria-controls.
        this._currentWindowTitleItem.setAriaAttribute("haspopup", "dialog");
        this._currentWindowTitleItem.setAriaAttribute("controls", context.MainContainerService.getFloatingPanel()?.id);
        this._currentWindowTitleItem.setAriaAttribute("expanded", false);
      } else {
        this._currentWindowTitleItem.setAriaAttribute("haspopup", null);
        this._currentWindowTitleItem.setAriaAttribute("controls", null);
        this._currentWindowTitleItem.setAriaAttribute("expanded", null);
      }
    }

    /**
     * Create a window title item and add in chromebar title
     * @private
     */
    _createCurrentWindowTitleItem() {
      // Need to find a better location to create that widget
      if (gbc.LogService.isRecordingEnabled() && !this._logRecordWidget) {
        this._logRecordWidget = cls.WidgetFactory.createWidget("LogRecorder", {
          appHash: gbc.systemAppId
        });
      }
      this._currentWindowTitleItem = cls.WidgetFactory.createWidget("Item", {
        appHash: gbc.systemAppId
      });
      // GBC-5118 Disable window change temporary
      this._currentWindowTitleItem.onClick(() => {
        this.displayWindowsList(context.ApplicationListService.getCurrentAppItem().getApplicationIdentifier());
      });
      this._currentWindowTitleItem.setSecondaryIcon("zmdi-chevron-down");
      this._currentWindowTitleItem.setQAInfo("name", "gbc_chromeBarTitle");
      const middleElements = [this._currentWindowTitleItem.getElement()];
      if (this._logRecordWidget) {
        middleElements.push(this._logRecordWidget.getElement());
      }
      context.MainContainerService.setChromeBarMiddleContent(...middleElements);
    }

    /**
     * Disable the window list and window title item widget
     */
    disableWindowList() {
      if (this._currentWindowTitleItem && !this._currentWindowTitleItem.isDestroyed()) {
        this._currentWindowTitleItem.destroy();
      }
      this._currentWindowTitleItem = null;
    }
  }

  gbc.WindowListService = new WindowListService();

  context.InitService.register(context.WindowListService);
});
